import { useMutation, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '@/lib/queryKeys'

import { updateAddress } from '../addressService'
import type { Address, UpdateAddressPayload } from '../types/Address'

export const useUpdateAddress = (userId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ addressId, address }: { addressId: string; address: UpdateAddressPayload }) => updateAddress(addressId, address),
    onMutate: async ({ addressId, address }) => {
      if (!userId) return
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.ADDRESSES, userId] })

      // Snapshot the previous value
      const previousAddresses = queryClient.getQueryData<Address[]>([QUERY_KEYS.ADDRESSES, userId])

      // Optimistically update to the new value
      queryClient.setQueryData<Address[]>([QUERY_KEYS.ADDRESSES, userId], old =>
        old?.map(addr => {
          if (addr.id === addressId) {
            return {
              ...addr,
              ...address,
              updatedAt: new Date().toISOString(),
            }
          }
          return addr
        })
      )

      // Return a context object with the snapshotted value
      return { previousAddresses }
    },
    onError: (_error, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousAddresses) {
        queryClient.setQueryData([QUERY_KEYS.ADDRESSES, userId], context.previousAddresses)
      }
    },
    onSuccess: updatedAddress => {
      // Replace optimistic update with real one from server
      queryClient.setQueryData<Address[]>([QUERY_KEYS.ADDRESSES, userId], old =>
        old?.map(addr => (addr.id === updatedAddress.id ? updatedAddress : addr))
      )
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADDRESSES] })
    },
  })
}
